import React from 'react'
import {
  Listbox,
  ListboxProps,
  ListboxInput,
  ListboxButton,
  ListboxPopover,
  ListboxList,
  ListboxOption,
} from '@reach/listbox'

import NativeSelect, {
  NativeSelectWrapper,
  StyledChevronsUpDownIcon,
} from './NativeSelect.styles'
import Breakpoint from 'components/Breakpoint'

/**
 * Types
 */
interface SelectProps extends ListboxProps {
  disabled?: boolean
  label?: React.ReactNode
  /**
   * Provide the list of options for the mobile version of the select (Native select).
   * The <Select> component switches to NativeSelect under the hood on small viewports.
   * Normally Select has a hacky mechanism to build this optionsForNativeSelect list from
   * the children but this does not work for more complex structure (fragments, options with children
   * that are ReactNodes instead of strings...). This prop allows you to overwrite this hacky parsing
   * by providing manually the list of possible options.
   */
  optionsForNativeSelect?: NativeOptionConfig[]
  placeholder?: string
  invalid?: boolean
}

/** Reads the children of this Select component and returns the strings for
 * the <option> tags as well as their onClick action function
 */
export interface NativeOptionConfig {
  value?: string
  name?: string
  action?: () => void
}

/**
 * Helpers
 */
const getOptions = (children: React.ReactNode): NativeOptionConfig[] => {
  const options: NativeOptionConfig[] = []
  React.Children.forEach(children, child => {
    if (
      child &&
      typeof child === 'object' &&
      'props' in child &&
      typeof child?.props?.children === 'string'
    ) {
      options.push({
        value: child.props.value,
        name: child.props.children,
        action: child.props.onClick,
      })
    }
  })

  return options
}

/**
 * Returns a function that gets the currently select `<option>` value
 * This function finds the corresponding `onClick` action function and calls it
 */
const selectNativeOption = (options: NativeOptionConfig[]) => (
  e: React.ChangeEvent<HTMLSelectElement>
) => {
  const value = e?.target.value
  const selectedOption = options.find(opt => opt.value === value)
  selectedOption?.action?.()
}

// Components
const Select = ({
  disabled,
  label,
  optionsForNativeSelect,
  placeholder,
  invalid,
  value,
  ...props
}: SelectProps) => (
  <Breakpoint on="sm">
    {notSmall => {
      if (notSmall || disabled) {
        return (
          <Listbox
            arrow={<StyledChevronsUpDownIcon />}
            disabled={disabled}
            data-invalid={invalid}
            value={value ? value : placeholder}
            {...props}
            data-placeholder={placeholder && !value}>
            {placeholder && (
              <ListboxOption
                key={
                  placeholder /* We use the key to force the option to update when the placeholder changes */
                }
                value={placeholder}
                aria-disabled="true">
                {placeholder}
              </ListboxOption>
            )}
            {props.children}
          </Listbox>
        )
      } else {
        // Mobile version - uses native html elements so that the mobile OS
        // displays their native selectors
        const options = optionsForNativeSelect || getOptions(props.children)
        const isAnOptionSelected = options.some(
          opt => opt.value === props.defaultValue
        )
        return (
          <NativeSelectWrapper>
            <StyledChevronsUpDownIcon />
            <NativeSelect
              data-invalid={invalid}
              onChange={event => {
                selectNativeOption(options)(event)
                props.onChange?.(event.target.value)
              }}>
              {!optionsForNativeSelect &&
                (!isAnOptionSelected || label || placeholder) && (
                  // Show the label instead of the selected option
                  <option value="" disabled={true} selected={true}>
                    {label || placeholder}
                  </option>
                )}
              {options.map(({ value: optionValue, name }) => (
                <option
                  key={optionValue}
                  value={optionValue}
                  selected={value === optionValue}>
                  {name}
                </option>
              ))}
            </NativeSelect>
          </NativeSelectWrapper>
        )
      }
    }}
  </Breakpoint>
)

export {
  Select,
  ListboxInput as SelectInput,
  ListboxButton as SelectTrigger,
  ListboxPopover as SelectPopover,
  ListboxList as SelectList,
  ListboxOption as SelectOption,
}
